import { useQuery } from "@apollo/client/react";
import {
  ContractTypesDocument,
  CountriesDocument,
  DegreesDocument,
  GendersDocument,
  IndustriesDocument,
  JobDepartmentsDocument,
  JobLocationsDocument,
  MaritalStatusesDocument,
  WorkingTimeModelsDocument,
} from "@/graphql/generated/operations";

export interface ReferenceOption {
  value: string;
  label: string;
  /** Stable canonical English name (locale-independent), when the entity exposes one. */
  nameEn?: string | null;
}

export interface UseProfileReferenceDataResult {
  countries: ReferenceOption[];
  genders: ReferenceOption[];
  maritalStatuses: ReferenceOption[];
  jobDepartments: ReferenceOption[];
  jobLocations: ReferenceOption[];
  industries: ReferenceOption[];
  degrees: ReferenceOption[];
  contractTypes: ReferenceOption[];
  workingTimeModels: ReferenceOption[];
  loading: boolean;
}

// Profile-page reference data (countries, genders, marital statuses, and the
// Current occupation reference lists: departments, locations, contract types,
// working-time models). Bundled because ProfilePage uses them together;
// co-loading is fine because each is a single small list and Apollo
// dedupes cross-page reads.
export function useProfileReferenceData(): UseProfileReferenceDataResult {
  const countriesQ = useQuery(CountriesDocument);
  const gendersQ = useQuery(GendersDocument);
  const maritalQ = useQuery(MaritalStatusesDocument);
  const jobDepartmentsQ = useQuery(JobDepartmentsDocument);
  const jobLocationsQ = useQuery(JobLocationsDocument);
  const contractTypesQ = useQuery(ContractTypesDocument);
  const workingTimeModelsQ = useQuery(WorkingTimeModelsDocument);
  const industriesQ = useQuery(IndustriesDocument);
  const degreesQ = useQuery(DegreesDocument);

  const countries: ReferenceOption[] = (countriesQ.data?.countries.countries ?? []).map((c) => ({
    value: c.id,
    label: c.name ?? c.iso2 ?? c.id,
  }));
  const genders: ReferenceOption[] = (gendersQ.data?.genders.genders ?? []).map((g) => ({
    value: g.id,
    label: g.name ?? g.id,
    nameEn: g.nameEn,
  }));
  const maritalStatuses: ReferenceOption[] = (
    maritalQ.data?.maritalStatuses.maritalStatuses ?? []
  ).map((m) => ({
    value: m.id,
    label: m.name ?? m.id,
    nameEn: m.nameEn,
  }));
  const jobDepartments: ReferenceOption[] = (
    jobDepartmentsQ.data?.jobDepartments.jobDepartments ?? []
  ).map((d) => ({
    value: d.id,
    label: d.localizedName?.localized ?? d.name ?? d.id,
  }));
  const jobLocations: ReferenceOption[] = (
    jobLocationsQ.data?.jobLocations.jobLocations ?? []
  ).map((l) => ({
    value: l.id,
    label: l.localizedName?.localized ?? l.name ?? l.id,
  }));
  const contractTypes: ReferenceOption[] = (
    contractTypesQ.data?.contractTypes.contractTypes ?? []
  ).map((c) => ({
    value: c.id,
    label: c.localizedName?.localized ?? c.name ?? c.id,
  }));
  const workingTimeModels: ReferenceOption[] = (
    workingTimeModelsQ.data?.workingTimeModels.workingTimeModels ?? []
  ).map((w) => ({
    value: w.id,
    label: w.localizedName?.localized ?? w.name ?? w.id,
  }));
  const industries: ReferenceOption[] = (
    industriesQ.data?.industries.industries ?? []
  ).map((i) => ({
    value: i.id,
    label: i.localizedName?.localized ?? i.name ?? i.id,
  }));
  const degrees: ReferenceOption[] = (
    degreesQ.data?.degrees.degrees ?? []
  ).map((degree) => ({
    value: degree.id,
    label: degree.localizedName?.localized ?? degree.name ?? degree.id,
    nameEn: degree.name,
  }));

  return {
    countries,
    genders,
    maritalStatuses,
    jobDepartments,
    jobLocations,
    contractTypes,
    workingTimeModels,
    industries,
    degrees,
    loading:
      countriesQ.loading ||
      gendersQ.loading ||
      maritalQ.loading ||
      jobDepartmentsQ.loading ||
      jobLocationsQ.loading ||
      contractTypesQ.loading ||
      workingTimeModelsQ.loading ||
      industriesQ.loading ||
      degreesQ.loading,
  };
}
